import loadable from '@loadable/component';
import Login from '@components/Login/Login';
import FullErrorPage from '@components/FullErrorPage/FullErrorPage';
import {Unauthorized} from '@components/shared/ErrorPages/Unauthorized';
import loginSsrFetch from '@components/Login/ssrFetch';
import dashboardSsrFetch from '@components/Dashboard/ssrFetch';
import transactionsSsrFetch from '@components/Transactions/ssrFetch';
import archiveSsrFetch from '@components/Archive/ssrFetch';
import archiveDetailSsrFetch from '@components/Archive/ssrFetchDetail';
import transactionSsrFetch from '@components/Transaction/ssrFetch';
import transactionDesignerSsrFetch from '@components/Designer/transactionSsrFetch';
import templateDesignerSsrFetch from '@components/Designer/templateSsrFetch';
import templatesSsrFetch from '@components/Templates/ssrFetch';
import templateDetailsSsrFetch from '@components/Template/ssrFetch';
import {
    LOGIN_PATH,
    FORCE_LOGIN_PATH,
    DASHBOARD_PATH,
    PASSWORD_RECOVERY_PATH,
    TRANSACTIONS_PATH,
    ARCHIVE_PATH,
    ARCHIVE_REQUEST_PATH,
    TRANSACTION_PATH,
    TEMPLATES_PATH,
    TEMPLATE_PATH,
    ACCOUNT_PATH,
    DESIGNER_PATH,
    ADMIN_PATH,
    PASSWORD_RESET_PATH,
    REGISTRATION_PATH,
    USER_REGISTRATION_PATH,
    ACCOUNT_REGISTRATION_PATH,
    REPORTS_PATH,
    DESIGN_PATH,
    ERROR_PATH,
    FAST_TRACK_PATH,
    SENDER_REPORT_PATH,
    SSO_AUTH_PATH,
    CONNECT_AUTHORIZE_PATH,
    EMAIL_ALIAS_CONFIRM_PATH
} from '@constants';
import AuthenticationPage from '@components/AuthenticationPage/AuthenticationPage';
import withScopeGuard from '@components/withScopeGuard';
import permissionsService from '@utils/permissionsService';
import LoginForms from '@components/Login/LoginForms/LoginForms';
import PasswordRecovery from '@components/PasswordRecovery/PasswordRecovery';
import PasswordReset from '@components/PasswordReset/PasswordReset';
import Registration from '@components/Login/Registration/Registration';
import UsageSummary from '@components/Reports/UsageSummary/UsageSummary';
import SenderSummary from '@components/Reports/UsageSummary/SenderSummary/SenderSummary';
import accountRoutes from './accountRoutes';
import adminRoutes from './adminRoutes';
import {getMainRouteConfig} from './routeConfigs';


export const mainRoutes = [
    {
        path: LOGIN_PATH,
        component: Login,
        fetchData: loginSsrFetch,
        ...getMainRouteConfig(LOGIN_PATH),
        childrenRoutes: [{
            index: true,
            component: LoginForms
        }]
    }, {
        path: FORCE_LOGIN_PATH,
        component: Login,
        fetchData: loginSsrFetch,
        ...getMainRouteConfig(FORCE_LOGIN_PATH),
        childrenRoutes: [{
            index: true,
            component: LoginForms
        }]
    }, {
        path: PASSWORD_RECOVERY_PATH,
        component: Login,
        fetchData: loginSsrFetch,
        ...getMainRouteConfig(PASSWORD_RECOVERY_PATH),
        childrenRoutes: [{
            index: true,
            component: PasswordRecovery
        }]
    }, {
        path: `${PASSWORD_RESET_PATH}/*`,
        component: Login,
        fetchData: loginSsrFetch,
        ...getMainRouteConfig(`${PASSWORD_RESET_PATH}/*`),
        childrenRoutes: [{
            index: true,
            component: PasswordReset
        }]
    }, {
        path: PASSWORD_RESET_PATH,
        component: Login,
        fetchData: loginSsrFetch,
        ...getMainRouteConfig(PASSWORD_RESET_PATH),
        childrenRoutes: [{
            index: true,
            component: PasswordReset
        }]
    }, {
        path: `${REGISTRATION_PATH}/*`,
        component: loadable(() => import(/* webpackChunkName: "login" */ '@components/Login/Login')),
        fetchData: loginSsrFetch,
        ...getMainRouteConfig(`${REGISTRATION_PATH}/*`),
        childrenRoutes: [{
            index: true,
            component: Registration
        }]
    }, {
        path: `${USER_REGISTRATION_PATH}/:registrationId`,
        component: loadable(() => import(/* webpackChunkName: "user-registration" */ '@components/UserRegistration/UserRegistration')),
        ...getMainRouteConfig(`${USER_REGISTRATION_PATH}/:registrationId`),
        childrenRoutes: [{
            index: true,
            component: loadable(() => import(/* webpackChunkName: "user-registration" */ '@components/UserRegistration/RegistrationVerify/RegistrationVerify'))
        }]
    }, {
        path: `${ACCOUNT_REGISTRATION_PATH}/*`,
        component: loadable(() => import(
            /* webpackChunkName: "account-registration" */
            '@components/AccountRegistration/AccountRegistrationLayout/AccountRegistrationLayout'
        )),
        ...getMainRouteConfig(`${ACCOUNT_REGISTRATION_PATH}/*`),
        childrenRoutes: [
            {
                index: true,
                component: loadable(() => import(
                    /* webpackChunkName: "account-registration" */
                    '@components/AccountRegistration/AccountRegistrationForm/AccountRegistrationForm'
                ))
            },
            {
                path: 'check-email',
                component: loadable(() => import(
                    /* webpackChunkName: "account-registration" */
                    '@components/AccountRegistration/CheckEmail/CheckEmail'
                ))
            },
            {
                path: ':registrationId/confirm',
                component: loadable(() => import(
                    /* webpackChunkName: "account-registration" */
                    '@components/AccountRegistration/RegistrationCompletion/RegistrationCompletion'
                ))
            },
            {
                path: 'set-password',
                component: loadable(() => import(
                    /* webpackChunkName: "account-registration" */
                    '@components/AccountRegistration/SetPassword/SetPassword'
                ))
            }
        ]
    }, {
        path: ERROR_PATH,
        component: FullErrorPage,
        ...getMainRouteConfig(ERROR_PATH)
    }, {
        path: '/access-denied',
        component: Unauthorized,
        ...getMainRouteConfig('/access-denied')
    }, {
        path: DASHBOARD_PATH,
        component: loadable(() => import(
            /* webpackChunkName: "main-pages" */
            /* webpackPrefetch: true */
            '@components/Dashboard/Dashboard'
        )),
        fetchData: dashboardSsrFetch,
        ...getMainRouteConfig(DASHBOARD_PATH)
    }, {
        path: `${TRANSACTIONS_PATH}/:query?`,
        component: withScopeGuard(loadable(
            () => import(/* webpackChunkName: "main-pages" */ '@components/Transactions/Transactions')
        )),
        fetchData: transactionsSsrFetch,
        ...getMainRouteConfig(`${TRANSACTIONS_PATH}/:query?`),
        isEnabled: permissionsService.hasTransactionPermission
    }, {
        path: ARCHIVE_PATH,
        component: loadable(() => import(/* webpackChunkName: "main-pages" */ '@components/Archive/Archive')),
        fetchData: archiveSsrFetch,
        ...getMainRouteConfig(ARCHIVE_PATH),
        isEnabled: permissionsService.hasArchivePermission
    }, {
        path: ARCHIVE_REQUEST_PATH,
        component: loadable(() => import(/* webpackChunkName: "main-pages" */ '@components/Archive/ArchiveDetail')),
        fetchData: archiveDetailSsrFetch,
        ...getMainRouteConfig(ARCHIVE_REQUEST_PATH),
        isEnabled: permissionsService.hasArchivePermission
    }, {
        path: `${TRANSACTION_PATH}/:packageId${DESIGNER_PATH}/:subLink?`,
        component: withScopeGuard(loadable(
            () => import(/* webpackChunkName: "designer" */ '@components/Designer/Designer')
        )),
        fetchData: transactionDesignerSsrFetch,
        ...getMainRouteConfig(`${TRANSACTION_PATH}/:packageId${DESIGNER_PATH}/:subLink?`),
        end: true,
        isEnabled: permissionsService.hasTransactionPermission
    },
    {
        path: `${TRANSACTION_PATH}/:packageId${DESIGN_PATH}/:subLink?`,
        component: withScopeGuard(loadable(
            () => import(/* webpackChunkName: "designer" */ '@components/Designer/Designer')
        )),
        fetchData: transactionDesignerSsrFetch,
        ...getMainRouteConfig(`${TRANSACTION_PATH}/:packageId${DESIGN_PATH}/:subLink?`),
        end: true,
        isEnabled: permissionsService.hasTransactionPermission
    }, {
        path: `${TRANSACTION_PATH}/:packageId?`,
        component: withScopeGuard(loadable(
            () => import(/* webpackChunkName: "main-pages" */ '@components/TransactionPageWrapper/TransactionPageWrapper')
        )),
        fetchData: transactionSsrFetch,
        ...getMainRouteConfig(`${TRANSACTION_PATH}/:packageId?`),
        end: true,
        isEnabled: permissionsService.hasTransactionPermission
    }, {
        path: `${TEMPLATE_PATH}/:packageId${DESIGNER_PATH}/:subLink?`,
        component: withScopeGuard(loadable(
            () => import(/* webpackChunkName: "designer" */ '@components/Designer/Designer')
        )),
        fetchData: templateDesignerSsrFetch,
        ...getMainRouteConfig(`${TEMPLATE_PATH}/:packageId${DESIGNER_PATH}/:subLink?`),
        end: true,
        isEnabled: permissionsService.hasTransactionPermission
    },
    {
        path: `${TEMPLATE_PATH}/:packageId${DESIGN_PATH}/:subLink?`,
        component: withScopeGuard(loadable(
            () => import(/* webpackChunkName: "designer" */ '@components/Designer/Designer')
        )),
        fetchData: templateDesignerSsrFetch,
        ...getMainRouteConfig(`${TEMPLATE_PATH}/:packageId${DESIGN_PATH}/:subLink?`),
        end: true,
        isEnabled: permissionsService.hasTransactionPermission
    }, {
        path: TEMPLATES_PATH,
        component: withScopeGuard(loadable(() => import(/* webpackChunkName: "main-pages" */ '@components/Templates/Templates'))),
        fetchData: templatesSsrFetch,
        ...getMainRouteConfig(TEMPLATES_PATH),
        isEnabled: permissionsService.hasTransactionPermission
    },
    {
        path: `${TEMPLATE_PATH}/:packageId?`,
        component: withScopeGuard(loadable(() => import(/* webpackChunkName: "main-pages" */ '@components/TemplatePageWrapper/TemplatePageWrapper'))),
        fetchData: templateDetailsSsrFetch,
        ...getMainRouteConfig(`${TEMPLATE_PATH}/:packageId?`)
    }, {
        path: ACCOUNT_PATH,
        component: loadable(
            () => import(/* webpackChunkName: "account" */ '@components/AccountSettings/AccountSettings')
        ),
        ...getMainRouteConfig(ACCOUNT_PATH),
        isEnabled: permissionsService.isNotDelegating,
        childrenRoutes: accountRoutes
    },
    {
        path: ADMIN_PATH,
        component: loadable(
            () => import(/* webpackChunkName: "admin" */ '@components/AdminPanel/AdminPanel')
        ),
        ...getMainRouteConfig(ADMIN_PATH),
        isEnabled: permissionsService.isNotDelegating,
        childrenRoutes: adminRoutes
    }, {
        path: REPORTS_PATH,
        component: loadable(
            () => import(/* webpackChunkName: "secondary-pages" */ '@components/Reports/Reports')
        ),
        ...getMainRouteConfig(REPORTS_PATH),
        isEnabled: (params) => permissionsService.isNotDelegating(params)
            && permissionsService.hasReportPagePermission(params),
        childrenRoutes: [{
            path: `${REPORTS_PATH}/:query?`,
            component: UsageSummary,
            index: true
        }, {
            path: `${REPORTS_PATH}${SENDER_REPORT_PATH}`,
            component: SenderSummary
        }]
    }, {
        path: `${FAST_TRACK_PATH}`,
        component: loadable(
            () => import(/* webpackChunkName: "secondary-pages" */ '@components/Templates/FastTrack/FastTrackPage/FastTrackPage')
        ),
        ...getMainRouteConfig(FAST_TRACK_PATH)
    }, {
        path: `${SSO_AUTH_PATH}/*`,
        component: AuthenticationPage,
        end: false,
        ...getMainRouteConfig(`${SSO_AUTH_PATH}/*`)
    }, {
        path: CONNECT_AUTHORIZE_PATH,
        component: loadable(() => import(/* webpackChunkName: "connect-consent" */ '@components/ConnectConsent/ConnectConsent')),
        ...getMainRouteConfig(CONNECT_AUTHORIZE_PATH)
    }, {
        path: EMAIL_ALIAS_CONFIRM_PATH,
        component: loadable(() => import(/* webpackChunkName: "email-alias-confirm" */ '@components/EmailAliasConfirm/EmailAliasConfirm')),
        ...getMainRouteConfig(EMAIL_ALIAS_CONFIRM_PATH)
    }
];

export default mainRoutes;
